import { wrapIndex } from '../core/math';
import { smoothSeries } from './geometry';

/**
 * Vertical shape for the real circuits — see docs/circuit-fidelity.md.
 *
 * Everything here operates on *fraction of lap* (0..1, wrapping across the
 * start/finish seam) rather than centerline index, because a `RealCircuit`'s
 * height and banking are authored once against its real-world shape while its
 * point count changes at every stage of `traceCircuit`'s spline/resample/
 * curvature-limit pipeline. Sampling by fraction means the authored data
 * never has to be re-indexed when that pipeline changes.
 *
 * Every function here is plain arithmetic — no trig, no RNG, no clock — so a
 * named circuit's height and banking regenerate byte-identically forever, the
 * same guarantee `points` itself already carries.
 */

export type RunoffKind = 'grass' | 'gravel' | 'runoffAsphalt';

/** `[fraction of lap 0..1, value]`. */
export type SeriesKeyframe = readonly [number, number];

/**
 * Piecewise-linear interpolation of keyframes over a closed 0..1 loop, sampled
 * at `n` evenly spaced points (index `i` -> fraction `i/n`).
 *
 * The keyframe list is tripled at `t-1`, `t` and `t+1` so a query never has to
 * special-case the wraparound: whichever copy brackets the query fraction is
 * used, and every point in `[0, 1)` is covered by exactly one bracket.
 */
export function interpolateClosedSeries(keyframes: readonly SeriesKeyframe[], n: number): Float64Array {
  const out = new Float64Array(n);
  if (keyframes.length === 0) return out;
  const sorted = [...keyframes].sort((a, b) => a[0] - b[0]);
  const tripled: SeriesKeyframe[] = [];
  for (const offset of [-1, 0, 1]) {
    for (const [t, v] of sorted) tripled.push([t + offset, v]);
  }

  let seg = 0;
  for (let i = 0; i < n; i++) {
    const t = i / n;
    while (seg < tripled.length - 2 && tripled[seg + 1][0] <= t) seg++;
    const [t0, v0] = tripled[seg];
    const [t1, v1] = tripled[seg + 1];
    const frac = t1 > t0 ? (t - t0) / (t1 - t0) : 0;
    out[i] = v0 + (v1 - v0) * frac;
  }
  return out;
}

/**
 * Real-world elevation keyframes -> a per-index game-scale series.
 *
 * `factor` folds in both the circuit's horizontal shrink (`length /
 * realLength`) and its `verticalScale` — see docs/circuit-fidelity.md §2 for
 * why elevation needs a *second* factor on top of the horizontal one. A short
 * smoothing pass (the same `smoothSeries` the curvature signal already uses)
 * keeps a keyframe corner from reading as a kink once it lands on the
 * resampled centerline.
 */
export function buildElevationSeries(
  keyframes: readonly SeriesKeyframe[] | undefined,
  n: number,
  factor: number,
): Float64Array | undefined {
  if (!keyframes || keyframes.length === 0) return undefined;
  const raw = interpolateClosedSeries(keyframes, n);
  const scaled = new Float64Array(n);
  for (let i = 0; i < n; i++) scaled[i] = raw[i] * factor;
  return smoothSeries(scaled, 3);
}

/**
 * Banking keyframes are already authored as the reduced, final cross-slope
 * ratio to use in game (see docs/circuit-fidelity.md §5's Zandvoort note), so
 * no extra scale factor applies here beyond the same smoothing pass.
 */
export function buildBankSeries(
  keyframes: readonly SeriesKeyframe[] | undefined,
  n: number,
): Float64Array | undefined {
  if (!keyframes || keyframes.length === 0) return undefined;
  return smoothSeries(interpolateClosedSeries(keyframes, n), 3);
}

/**
 * Per-index run-off surface: `runoffDefault` (or grass) everywhere, overridden
 * inside the given `[fromFraction, toFraction, kind]` spans. A `to` less than
 * `from` wraps through the start/finish line. Spans apply in array order, so a
 * later one wins where two overlap.
 */
export function buildRunoffKindSeries(
  spans: readonly (readonly [number, number, RunoffKind])[] | undefined,
  n: number,
  runoffDefault: RunoffKind = 'grass',
): RunoffKind[] {
  const out: RunoffKind[] = new Array(n).fill(runoffDefault);
  if (!spans) return out;
  for (const [from, to, kind] of spans) {
    const a = Math.round(from * n);
    const b = Math.round(to * n);
    const end = b >= a ? b : b + n;
    for (let i = a; i < end; i++) out[wrapIndex(i, n)] = kind;
  }
  return out;
}
